"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { FiUser, FiLock, FiMail, FiMapPin, FiShield } from "react-icons/fi"
import Layout from "../../Layout"

const FALLBACK_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%235e17eb'/%3E%3Ctext x='20' y='25' text-anchor='middle' fill='white' font-size='16' font-family='Arial' dy='.3em'%3E%3C/text%3E%3C/svg%3E"

const algerianWilayas = [
  { name: "Adrar" }, { name: "Chlef" }, { name: "Laghouat" }, { name: "Oum El Bouaghi" },
  { name: "Batna" }, { name: "Béjaïa" }, { name: "Biskra" }, { name: "Béchar" },
  { name: "Blida" }, { name: "Bouira" }, { name: "Tamanrasset" }, { name: "Tébessa" },
  { name: "Tlemcen" }, { name: "Tiaret" }, { name: "Tizi Ouzou" }, { name: "Alger" },
  { name: "Djelfa" }, { name: "Jijel" }, { name: "Sétif" }, { name: "Saïda" },
  { name: "Skikda" }, { name: "Sidi Bel Abbès" }, { name: "Annaba" }, { name: "Guelma" },
  { name: "Constantine" }, { name: "Médéa" }, { name: "Mostaganem" }, { name: "M'Sila" },
  { name: "Mascara" }, { name: "Ouargla" }, { name: "Oran" }, { name: "El Bayadh" },
  { name: "Illizi" }, { name: "Bordj Bou Arréridj" }, { name: "Boumerdès" }, { name: "El Tarf" },
  { name: "Tindouf" }, { name: "Tissemsilt" }, { name: "El Oued" }, { name: "Khenchela" },
  { name: "Souk Ahras" }, { name: "Tipaza" }, { name: "Mila" }, { name: "Aïn Defla" },
  { name: "Naâma" }, { name: "Aïn Témouchent" }, { name: "Ghardaïa" }, { name: "Relizane" },
]

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`

const ProfilePage = () => {
  const [authData, setAuthData] = useState(JSON.parse(localStorage.getItem("auth_data") || "{}"))
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [address, setAddress] = useState("")
  const [role, setRole] = useState("")
  const [photo, setPhoto] = useState(null)
  const [photoPreview, setPhotoPreview] = useState(null)
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState("personal")
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(FALLBACK_IMAGE)
  const navigate = useNavigate()

  const getToken = () => localStorage.getItem("access_token")

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return FALLBACK_IMAGE
    if (photoPath.startsWith("http://") || photoPath.startsWith("https://")) {
      return photoPath
    }
    if (photoPath.startsWith(backend_url)) {
      return photoPath
    }
    if (photoPath.startsWith('/media')) {
      return `${backend_url}${photoPath}`
    }
    if (photoPath.startsWith('/uploads')) {
      return `${backend_url}${photoPath}`
    }
    return `${backend_url}/media/${photoPath}`
  }

  // Charger les données utilisateur
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!authData.userId || !getToken()) {
          return
        }

        console.log("Chargement des données pour userId:", authData.userId)
        
        const response = await fetch(`${API_URL}/users/${authData.userId}/`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        })

        if (response.ok) {
          const userData = await response.json()
          console.log("Données reçues:", userData)
          
          setName(userData.name || "")
          setEmail(userData.email || "")
          setAddress(userData.address || "")
          setRole(userData.role_name || userData.role?.name || "")
          
          // Use photo_url from response
          let photoFullUrl = FALLBACK_IMAGE
          if (userData.photo_url) {
            photoFullUrl = getPhotoUrl(userData.photo_url)
          } else if (userData.photo) {
            photoFullUrl = getPhotoUrl(userData.photo)
          }
          
          console.log("Photo chargée:", photoFullUrl)
          setCurrentPhotoUrl(photoFullUrl)
          setPhotoPreview(photoFullUrl)
          
          // Update authData in localStorage
          const updatedAuthData = {
            ...authData,
            name: userData.name,
            address: userData.address,
            role: userData.role_name || userData.role?.name,
            photo: photoFullUrl,
            email: userData.email,
          }
          localStorage.setItem("auth_data", JSON.stringify(updatedAuthData))
          setAuthData(updatedAuthData)
          
          // Dispatch events
          window.dispatchEvent(new CustomEvent('user-profile-updated', { 
            detail: updatedAuthData 
          }))
          window.dispatchEvent(new Event('storage'))
          
        } else {
          const errorData = await response.json()
          console.error("Erreur réponse:", errorData)
          setError("Erreur lors du chargement du profil")
        }
      } catch (err) {
        console.error("Erreur fetch:", err)
        setError("Erreur de connexion au serveur")
      }
    }

    fetchUserData()
  }, [authData.userId])

  const handlePhotoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError("La photo ne doit pas dépasser 2MB")
        return
      }
      if (!file.type.startsWith('image/')) {
        setError("Le fichier doit être une image")
        return
      }
      
      setPhoto(file)
      const previewUrl = URL.createObjectURL(file)
      setPhotoPreview(previewUrl)
      setError("")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")
    setLoading(true)

    if (!authData.userId || !getToken()) {
      setError("Vous devez être connecté")
      setLoading(false)
      return
    }

    // Validation des champs
    if (name.trim().length < 2) {
      setError("Le nom doit contenir au moins 2 caractères")
      setLoading(false)
      return
    }

    try {
      const hasPasswordChanges = newPassword || currentPassword || confirmPassword

      // 1. Mettre à jour le profil avec FormData
      const formData = new FormData()
      formData.append("name", name.trim())
      formData.append("address", address || "")
      
      if (email) {
        formData.append("email", email)
      }
      
      if (photo instanceof File) {
        formData.append("photo", photo)
        console.log("Photo ajoutée au formData:", photo.name, photo.size)
      }

      console.log("Envoi des données profil...")
      
      const profileResponse = await fetch(`${API_URL}/users/${authData.userId}/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      })

      const responseData = await profileResponse.json()
      console.log("Réponse du serveur:", responseData)

      if (!profileResponse.ok) {
        let errorMsg = "Erreur lors de la mise à jour"
        if (responseData.detail) errorMsg = responseData.detail
        else if (responseData.error) errorMsg = responseData.error
        else if (responseData.message) errorMsg = responseData.message
        throw new Error(errorMsg)
      }

      console.log("Profil mis à jour:", responseData)
      
      // Get the correct photo URL from response - IMPORTANT: use responseData.data
      let photoFullUrl = FALLBACK_IMAGE
      const userData = responseData.data || responseData
      
      if (userData.photo_url) {
        photoFullUrl = getPhotoUrl(userData.photo_url)
      } else if (userData.photo) {
        photoFullUrl = getPhotoUrl(userData.photo)
      }
      
      console.log("Photo finale:", photoFullUrl)
      
      // Update current photo URL
      setCurrentPhotoUrl(photoFullUrl)
      setPhotoPreview(photoFullUrl)
      
      // Mettre à jour localStorage
      const updatedAuthData = {
        ...authData,
        name: userData.name || name,
        address: userData.address || address,
        role: userData.role_name || userData.role?.name || role,
        photo: photoFullUrl,
        email: userData.email || email,
      }
      
      localStorage.setItem("auth_data", JSON.stringify(updatedAuthData))
      setAuthData(updatedAuthData)
      
      // Dispatch events to update header/sidebar
      window.dispatchEvent(new Event('storage'))
      window.dispatchEvent(new CustomEvent('user-profile-updated', { 
        detail: updatedAuthData 
      }))
      window.dispatchEvent(new CustomEvent('auth-updated'))

      // 2. Handle password change if needed
      if (hasPasswordChanges) {
        if (!currentPassword) {
          setError("Veuillez entrer votre mot de passe actuel")
          setLoading(false)
          return
        }
        if (!newPassword || !confirmPassword) {
          setError("Veuillez remplir tous les champs du mot de passe")
          setLoading(false)
          return
        }
        if (newPassword !== confirmPassword) {
          setError("Les nouveaux mots de passe ne correspondent pas")
          setLoading(false)
          return
        }
        if (newPassword.length < 8) {
          setError("Le mot de passe doit contenir au moins 8 caractères")
          setLoading(false)
          return
        }
        
        console.log("Changement du mot de passe...")
        
        const passwordResponse = await fetch(`${API_URL}/change-password/`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
          }),
        })
        
        const passwordData = await passwordResponse.json()
        console.log("Réponse mot de passe:", passwordData)
        
        if (!passwordResponse.ok) {
          throw new Error(passwordData.error || passwordData.detail || "Erreur lors du changement de mot de passe")
        }
      }

      // 3. Réinitialiser les champs
      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")
      setPhoto(null)
      
      const successMessage = hasPasswordChanges 
        ? "Profil et mot de passe mis à jour avec succès!"
        : "Profil mis à jour avec succès!"
      
      setSuccess(successMessage)
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess("")
      }, 3000)
      
    } catch (err) {
      console.error("Erreur complète:", err)
      setError(err.message || "Une erreur est survenue")
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setError("")
    setSuccess("")
  }

  return (
    <Layout>
      <div className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header du profil */}
          <div className="bg-white shadow-lg rounded-xl mb-8 overflow-hidden">
            <div className="h-32 bg-gradient-to-r from-[#56C596] to-[#5e17eb] relative"></div>
            <div className="px-6 py-5 flex justify-between items-center -mt-16">
              <div className="flex items-center">
                <div className="relative z-10">
                  <img
                    className="h-24 w-24 rounded-full object-cover border-4 border-white shadow-xl"
                    src={photoPreview || currentPhotoUrl || FALLBACK_IMAGE}
                    alt="Profile"
                    onError={(e) => {
                      console.error("Image failed to load:", e.target.src)
                      e.target.onerror = null
                      e.target.src = FALLBACK_IMAGE
                      setCurrentPhotoUrl(FALLBACK_IMAGE)
                      setPhotoPreview(FALLBACK_IMAGE)
                    }}
                  />
                  <label className="absolute bottom-0 right-0 bg-gradient-to-r from-[#56C596] to-[#5e17eb] rounded-full p-1.5 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300">
                    <FiUser className="h-4 w-4 text-white" />
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handlePhotoChange} 
                      className="hidden" 
                    />
                  </label>
                </div>
                <div className="ml-6 mt-14">
                  <h2 className="text-2xl font-bold text-gray-900">{name || "User Profile"}</h2>
                  <div className="flex flex-col space-y-1 mt-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <FiMail className="mr-2 h-4 w-4 text-[#5e17eb]" />
                      {email || "email@example.com"}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FiMapPin className="mr-2 h-4 w-4 text-[#5e17eb]" />
                      {address || "Aucune adresse"}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <FiShield className="mr-2 h-4 w-4 text-[#5e17eb]" />
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                        {role || "Aucun rôle"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg">
              <p className="text-sm font-medium">❌ Erreur</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}
          
          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-600 rounded-lg">
              <p className="text-sm font-medium">✅ Succès</p>
              <p className="text-sm mt-1">{success}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="bg-white shadow-lg rounded-xl overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6" aria-label="Tabs">
                <button
                  onClick={() => handleTabChange("personal")}
                  className={`py-4 px-3 border-b-2 ${
                    activeTab === "personal"
                      ? "border-[#5e17eb] text-[#5e17eb] font-semibold"
                      : "border-transparent text-gray-500 hover:text-[#5e17eb] hover:border-[#5e17eb]"
                  } font-medium text-sm transition-all duration-200 flex items-center`}
                >
                  <FiUser className={`mr-2 ${activeTab === "personal" ? "text-[#5e17eb]" : "text-gray-400"}`} />
                  Informations Personnelles
                </button>
                <button
                  onClick={() => handleTabChange("security")}
                  className={`py-4 px-3 border-b-2 ${
                    activeTab === "security"
                      ? "border-[#5e17eb] text-[#5e17eb] font-semibold"
                      : "border-transparent text-gray-500 hover:text-[#5e17eb] hover:border-[#5e17eb]"
                  } font-medium text-sm transition-all duration-200 flex items-center`}
                >
                  <FiLock className={`mr-2 ${activeTab === "security" ? "text-[#5e17eb]" : "text-gray-400"}`} />
                  Sécurité
                </button>
              </nav>
            </div>

            {/* Onglet Personnel */}
            <div className={`px-6 py-6 ${activeTab !== "personal" && "hidden"}`}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-6">
                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom Complet
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5e17eb] disabled:bg-gray-100"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      disabled={true}
                      className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>

                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ville
                    </label>
                    <select
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5e17eb] disabled:bg-gray-100"
                    >
                      <option value="">Sélectionnez une ville</option>
                      {algerianWilayas.map((wilaya) => (
                        <option key={wilaya.name} value={wilaya.name}>
                          {wilaya.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rôle
                    </label>
                    <input
                      type="text"
                      value={role}
                      disabled
                      className="w-full p-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-[#56C596] to-[#5e17eb] text-white rounded-xl hover:shadow-lg transition-all duration-300 font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Sauvegarde...
                      </>
                    ) : (
                      "Sauvegarder"
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Onglet Sécurité */}
            <div className={`px-6 py-6 ${activeTab !== "security" && "hidden"}`}>
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-6">
                Changer le Mot de Passe
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-6 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de Passe Actuel
                    </label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5e17eb] disabled:bg-gray-100"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nouveau Mot de Passe
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5e17eb] disabled:bg-gray-100"
                    />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmer
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5e17eb] disabled:bg-gray-100"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-gradient-to-r from-[#56C596] to-[#5e17eb] text-white rounded-xl hover:shadow-lg transition-all duration-300 font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Mise à jour...
                      </>
                    ) : (
                      "Mettre à Jour"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default ProfilePage